import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MultiDomainSelector } from "@/components/shared/MultiDomainSelector";
import { Book, ArrowLeft, ArrowRight } from "lucide-react";

interface EditBasicInfoProps {
  kbData: any;
  onUpdate: (updates: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const EditBasicInfo = ({ kbData, onUpdate, onNext, onBack }: EditBasicInfoProps) => {
  const [title, setTitle] = useState(kbData.title || "");
  const [description, setDescription] = useState(kbData.description || "");
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(kbData.domainIds || []);

  useEffect(() => {
    setTitle(kbData.title || "");
    setDescription(kbData.description || "");
    setSelectedDomainIds(kbData.domainIds || []);
  }, [kbData]);

  const handleNext = () => {
    onUpdate({
      title,
      description,
      domainIds: selectedDomainIds,
    });
    onNext();
  };

  const isValid = title.trim().length >= 3 && selectedDomainIds.length > 0;

  return (
    <Card className="border-2 border-black">
      <CardHeader className="bg-slate-100 border-b border-black">
        <CardTitle className="font-serif flex items-center">
          <Book className="mr-2" size={24} />
          Basic Information
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <Label htmlFor="title" className="text-base font-semibold">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter knowledge base title"
              className="mt-2"
            />
            <p className="text-sm text-gray-500 mt-1">
              Minimum 3 characters
            </p>
          </div>

          <div>
            <Label htmlFor="description" className="text-base font-semibold">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide a brief description of this knowledge base"
              rows={4}
              className="mt-2"
            />
          </div>

          <div>
            <Label className="text-base font-semibold mb-2 block">
              Knowledge Domains <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-gray-500 mb-3">
              Select one or more domain boxes that best categorize this knowledge
            </p>
            <MultiDomainSelector
              selectedDomainIds={selectedDomainIds}
              onDomainsChange={setSelectedDomainIds}
            />
          </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="border-2 border-black"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              disabled={!isValid}
              className="bg-black hover:bg-black/90"
            >
              Next: Additional Details
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
